"use client";

import type * as React from "react";

import { LINEAR_CLASSNAME } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

import { NotificationItemLayout } from "../../list/item/layout";
import type { Notification } from "../../../lib/types";

type ToastId = string | number;

export interface NotificationToastItemProps {
  notification: Notification;
  toastId: ToastId;
  onDismiss: (toastId: ToastId) => void;
}

function Root({ children, className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "group flex flex-col items-stretch rounded-lg font-sans",
        "w-80 max-sm:w-[calc(100vw-32px)]",
        "shadow-[var(--modal-shadow)]",
        "before:from-(--toast-accent)/20 before:to-(--toast-accent)/5",
        "border-composition",
        "[--toast-accent:#9bd4f8]",
        LINEAR_CLASSNAME,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface HeaderProps extends Pick<
  NotificationToastItemProps,
  "toastId" | "onDismiss"
> {
  iconName: IconName;
  iconStyle: string;
}

function Header({ iconName, toastId, onDismiss }: HeaderProps) {
  return (
    <div className="flex items-center gap-1.5 border-b border-border bg-surface-primary p-3 text-heading dark:bg-surface-elevated bg-[image:linear-gradient(180deg,transparent_0%,color-mix(in_srgb,var(--toast-accent)_20%,transparent)_100%)] dark:bg-[image:linear-gradient(180deg,transparent_0%,color-mix(in_srgb,var(--toast-accent)_10%,transparent)_100%)]">
      <Icon name={iconName} size={16} className="shrink-0 text-current" />
      <p className="flex-1 min-w-0 truncate text-[15px] font-medium leading-normal">
        Notifications
      </p>
      <DismissButton toastId={toastId} onDismiss={onDismiss} />
    </div>
  );
}

function Container({
  children,
  className,
  ...props
}: React.ComponentProps<typeof NotificationItemLayout.Container>) {
  return (
    <NotificationItemLayout.Container
      className={cn(
        "z-2 rounded-none border-x-0 border-b-0 border-t border-border bg-surface-primary px-3 py-2.5 shadow-none dark:bg-surface-elevated",
        "hover:bg-surface-hover dark:hover:bg-surface-elevated",
        className,
      )}
      {...props}
    >
      {children}
    </NotificationItemLayout.Container>
  );
}

function DismissButton({
  toastId,
  onDismiss,
}: Pick<NotificationToastItemProps, "toastId" | "onDismiss">) {
  return (
    <button
      type="button"
      aria-label="Dismiss notification toast"
      className="shrink-0 flex items-center justify-center w-4 h-4 cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
      onClick={(event) => {
        event.stopPropagation();
        onDismiss(toastId);
      }}
    >
      <Icon name="IconCrossMedium" size={16} className="text-heading" />
    </button>
  );
}

export const NotificationToastItemLayout = {
  ...NotificationItemLayout,
  Container,
  DismissButton,
  Header,
  Root,
};
